/**
 * Which page this URL is, without a router.
 *
 * Four routes, one bundle, and the navigation between them is a real `<a
 * href>` — a full document load. That is a deliberate choice rather than a
 * missing feature: the home page, the Front Range plate and the instrument do
 * not share a camera, a set of interaction handlers or a WebGL context worth
 * preserving, and the cost of the reload is a cached bundle and one
 * `conditions.json`. What it buys is that every page is a real URL that a
 * server, a crawler and a browser's back button all agree about, which is the
 * property architecture §5.4 actually asks for.
 *
 * The site is the project's home; each twin lives under its own path (spec
 * §4.1). The Front Range is the first. Anything unrecognised is the home page.
 * A 404 that is a blank screen is the worst of both worlds.
 */

export type Route = "home" | "landing" | "twin" | "about";

/** Every internal link in the app uses these, so a move is one edit. */
export const PATHS = {
  home: "/",
  landing: "/front-range",
  twin: "/front-range/twin",
  about: "/front-range/about",
} as const;

function normalise(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

export function routeOf(pathname: string): Route {
  const path = normalise(pathname);
  if (path === PATHS.landing) return "landing";
  if (path === PATHS.twin) return "twin";
  if (path === PATHS.about) return "about";
  return "home";
}

/**
 * The old top-level URLs (`/twin`, `/about`) keep working: they redirect to
 * their new home with the query and hash intact, so a bookmark to a gauge or
 * a shared camera still opens what it pointed at. `public/_redirects` does
 * the same for crawlers and for a browser with scripts off; this is the copy
 * that keeps the query. Null means the URL is current.
 */
export function redirectFor(pathname: string, search: string, hash: string): string | null {
  const path = normalise(pathname);
  const moved: Record<string, string> = { "/twin": PATHS.twin, "/about": PATHS.about };
  const to = moved[path];
  return to ? `${to}${search}${hash}` : null;
}

/** What the tab says. Set per route so a bookmark and a browser history entry
 * are legible. */
export const ROUTE_TITLE: Record<Route, string> = {
  home: "Bioregional Twin",
  landing: "Front Range — Bioregional Twin",
  twin: "The twin — Front Range Bioregional Twin",
  about: "Where this comes from — Front Range Bioregional Twin",
};
